import React from "react";
import {
  AbsoluteFill,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { COLORS, FONTS, SPRING } from "./constants";

function glow(color: string, strong = false): string {
  return strong
    ? `0 0 20px ${color}99, 0 0 50px ${color}50, 0 0 80px ${color}20`
    : `0 0 12px ${color}60, 0 0 30px ${color}25`;
}

const TABLE_ROWS = [
  {
    competitor: "Glean",
    price: "$15-25/seat",
    gap: "Can't serve 5-person teams",
    gapColor: COLORS.neonRed,
    highlight: false,
  },
  {
    competitor: "Guru",
    price: "$15/seat",
    gap: "No auto-ingest",
    gapColor: COLORS.neonOrange,
    highlight: false,
  },
  {
    competitor: "Notion AI",
    price: "$10/seat",
    gap: "No voice learning",
    gapColor: COLORS.neonYellow,
    highlight: false,
  },
  {
    competitor: "EAKC",
    price: "₹3K-15K/mo",
    gap: "Multi-domain, learns YOUR voice",
    gapColor: COLORS.neonGreen,
    highlight: true,
  },
];

const PRICING_TIERS = [
  {
    name: "Starter",
    price: "₹3K",
    color: COLORS.neonBlue,
    features: ["5 users", "3 domains", "Basic ingestion", "Email support"],
  },
  {
    name: "Pro",
    price: "₹8K",
    color: COLORS.neonPurple,
    features: ["25 users", "10 domains", "Voice learning", "Priority support"],
  },
  {
    name: "Enterprise",
    price: "₹15K",
    color: COLORS.neonPink,
    features: ["Unlimited users", "All domains", "Autonomous chains", "Dedicated CSM"],
  },
];

const TECH_BADGES = [
  { label: "Next.js 15", color: COLORS.neonCyan },
  { label: "Supabase",   color: COLORS.neonGreen },
  { label: "BullMQ",     color: COLORS.neonOrange },
  { label: "Docling",    color: COLORS.neonYellow },
  { label: "Tree-sitter",color: COLORS.neonPurple },
  { label: "OpenRouter", color: COLORS.neonPink },
];

export const Scene11Market: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  /* Exit fade */
  const fadeOut = interpolate(frame, [270, 300], [1, 0], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });

  /* Title */
  const titleSp = spring({ frame, fps, config: SPRING.snappy });
  const titleOp = interpolate(titleSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  const titleY  = interpolate(titleSp, [0, 1], [-40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  /* SME count animation 0 → 63 */
  const smeCountSp = spring({ frame: frame - 200, fps, config: SPRING.gentle });
  const smeCount   = Math.round(
    interpolate(smeCountSp, [0, 1], [0, 63], { extrapolateLeft: "clamp", extrapolateRight: "clamp" })
  );
  const smeOp = interpolate(smeCountSp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });

  return (
    <AbsoluteFill style={{ background: COLORS.bg, opacity: fadeOut }}>
      {/* Grid */}
      <div style={{
        position: "absolute", inset: 0,
        backgroundImage: `linear-gradient(${COLORS.neonBlue}06 1px, transparent 1px), linear-gradient(90deg, ${COLORS.neonBlue}06 1px, transparent 1px)`,
        backgroundSize: "60px 60px",
      }} />

      {/* Radial bg glow */}
      <div style={{
        position: "absolute", inset: 0,
        background: `radial-gradient(ellipse 80% 50% at 50% 30%, ${COLORS.neonBlue}08 0%, transparent 65%)`,
      }} />

      {/* Title */}
      <div style={{
        position: "absolute", top: 32, left: "50%",
        transform: `translateX(-50%) translateY(${titleY}px)`,
        opacity: titleOp, textAlign: "center",
      }}>
        <div style={{
          fontSize: 44,
          fontFamily: FONTS.heading,
          fontWeight: 800,
          background: `linear-gradient(90deg, ${COLORS.neonBlue}, ${COLORS.neonCyan})`,
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundClip: "text",
          filter: `drop-shadow(0 0 20px ${COLORS.neonBlue}60)`,
          letterSpacing: -1,
        }}>
          Market &amp; Technology
        </div>
      </div>

      {/* Market comparison table */}
      <div style={{
        position: "absolute",
        top: 100,
        left: "50%",
        transform: "translateX(-50%) perspective(1000px) rotateX(5deg)",
        width: 1100,
      }}>
        {/* Header */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 2fr",
          padding: "10px 24px",
          background: `${COLORS.bgCardLight}`,
          borderRadius: "12px 12px 0 0",
          border: `1px solid ${COLORS.neonBlue}40`,
          borderBottom: "none",
          gap: 16,
        }}>
          {["Competitor", "Price", "Gap / Advantage"].map((h) => (
            <div key={h} style={{
              fontSize: 13,
              fontFamily: FONTS.mono,
              fontWeight: 700,
              color: COLORS.textMuted,
              textTransform: "uppercase",
              letterSpacing: 1.5,
            }}>{h}</div>
          ))}
        </div>

        {/* Rows */}
        {TABLE_ROWS.map((row, i) => {
          const sp = spring({ frame: frame - (20 + i * 12), fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const x  = interpolate(sp, [0, 1], [-40, 0], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const isLast = i === TABLE_ROWS.length - 1;

          return (
            <div key={row.competitor} style={{
              opacity: op,
              transform: `translateX(${x}px)`,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 2fr",
              padding: "14px 24px",
              gap: 16,
              background: isLast
                ? `linear-gradient(90deg, ${COLORS.neonGreen}18, ${COLORS.neonGreen}08)`
                : i % 2 === 0 ? COLORS.bgCard : COLORS.bgCardLight,
              border: `1px solid ${isLast ? COLORS.neonGreen : COLORS.neonBlue}${isLast ? "60" : "20"}`,
              borderTop: "none",
              borderRadius: i === TABLE_ROWS.length - 1 ? "0 0 12px 12px" : 0,
              boxShadow: isLast ? `0 0 30px ${COLORS.neonGreen}30, 0 0 60px ${COLORS.neonGreen}10` : "none",
            }}>
              <div style={{
                fontSize: 16,
                fontFamily: FONTS.body,
                fontWeight: isLast ? 800 : 500,
                color: isLast ? COLORS.neonGreen : COLORS.textPrimary,
                textShadow: isLast ? glow(COLORS.neonGreen) : "none",
              }}>{row.competitor}</div>
              <div style={{
                fontSize: 16,
                fontFamily: FONTS.mono,
                color: COLORS.textSecondary,
              }}>{row.price}</div>
              <div style={{
                fontSize: 15,
                fontFamily: FONTS.body,
                fontWeight: isLast ? 700 : 400,
                color: row.gapColor,
                textShadow: isLast ? glow(row.gapColor, true) : glow(row.gapColor),
              }}>{row.gap}</div>
            </div>
          );
        })}
      </div>

      {/* SME stat */}
      <div style={{
        position: "absolute",
        top: 340,
        left: "50%",
        transform: "translateX(-50%)",
        opacity: smeOp,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: 48,
          fontFamily: FONTS.heading,
          fontWeight: 900,
          color: COLORS.neonGreen,
          textShadow: glow(COLORS.neonGreen, true),
        }}>
          {smeCount}M+ SMEs in India
        </div>
      </div>

      {/* Pricing tiers */}
      <div style={{
        position: "absolute",
        bottom: 90,
        left: 80,
        display: "flex",
        gap: 24,
      }}>
        {PRICING_TIERS.map((tier, i) => {
          const sp = spring({ frame: frame - (150 + i * 18), fps, config: SPRING.bouncy });
          const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const scale = interpolate(sp, [0, 1], [0.7, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
          const tilt = i === 0 ? "rotateY(8deg)" : i === 2 ? "rotateY(-8deg)" : "rotateY(0deg)";

          return (
            <div key={tier.name} style={{
              opacity: op,
              transform: `scale(${scale}) perspective(600px) ${tilt}`,
              width: 220,
              background: `linear-gradient(135deg, ${tier.color}18, ${COLORS.bgCard}ee)`,
              border: `1.5px solid ${tier.color}60`,
              borderRadius: 14,
              padding: "18px 20px",
              boxShadow: `0 0 24px ${tier.color}30, 0 0 50px ${tier.color}10`,
            }}>
              <div style={{
                fontSize: 13,
                fontFamily: FONTS.mono,
                fontWeight: 700,
                color: tier.color,
                textTransform: "uppercase",
                letterSpacing: 2,
                marginBottom: 6,
                textShadow: glow(tier.color),
              }}>{tier.name}</div>
              <div style={{
                fontSize: 30,
                fontFamily: FONTS.heading,
                fontWeight: 900,
                color: tier.color,
                textShadow: glow(tier.color, true),
                marginBottom: 12,
              }}>{tier.price}<span style={{ fontSize: 13, color: COLORS.textMuted, fontWeight: 400 }}>/mo</span></div>
              {tier.features.map((f) => (
                <div key={f} style={{
                  fontSize: 12,
                  fontFamily: FONTS.body,
                  color: COLORS.textSecondary,
                  marginBottom: 5,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}>
                  <span style={{ color: tier.color, fontSize: 10 }}>▸</span>
                  {f}
                </div>
              ))}
            </div>
          );
        })}
      </div>

      {/* Tech stack badges */}
      <div style={{
        position: "absolute",
        bottom: 90,
        right: 80,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        alignItems: "flex-end",
      }}>
        <div style={{
          fontSize: 12,
          fontFamily: FONTS.mono,
          color: COLORS.textMuted,
          letterSpacing: 1.5,
          textTransform: "uppercase",
          marginBottom: 4,
        }}>Tech Stack</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "flex-end", maxWidth: 380 }}>
          {TECH_BADGES.map((b, i) => {
            const sp = spring({ frame: frame - (150 + i * 14), fps, config: SPRING.snappy });
            const op = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            const scale = interpolate(sp, [0, 1], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
            return (
              <div key={b.label} style={{
                opacity: op,
                transform: `scale(${scale})`,
                padding: "7px 16px",
                borderRadius: 20,
                background: `${b.color}18`,
                border: `1.5px solid ${b.color}70`,
                fontFamily: FONTS.mono,
                fontSize: 13,
                fontWeight: 700,
                color: b.color,
                boxShadow: glow(b.color),
                whiteSpace: "nowrap",
              }}>
                {b.label}
              </div>
            );
          })}
        </div>
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
